import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import { LumiMascot } from "../../components/Brand";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const SUBJECTS = ["math", "reading", "writing", "science", "social_studies", "language"];
const AGES = ["5-7", "8-10", "11-13", "14-18"];
const GRADES = ["K","1","2","3","4","5","6","7","8","9","10","11","12"];

export default function Onboarding() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    display_name: user?.profile?.display_name || user?.full_name?.split(" ")[0] || "",
    age_range: user?.profile?.age_range || "8-10",
    grade_level: user?.profile?.grade_level || "3",
    country: user?.profile?.country || "USA",
    language: user?.profile?.language || "English",
    subjects: user?.profile?.subjects || ["math"],
    learning_pace: user?.profile?.learning_pace || "steady",
    parent_code: "",
  });

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function toggleSubject(s) {
    setForm((f) => ({
      ...f,
      subjects: f.subjects.includes(s) ? f.subjects.filter((x) => x !== s) : [...f.subjects, s],
    }));
  }

  async function finish() {
    try {
      await api.put("/auth/profile", form);
      await refresh();
      toast.success(t("onboarding.saved"));
      nav("/app/student");
    } catch (e) { toast.error(t("errors.generic")); }
  }

  const steps = [
    (
      <>
        <div className="font-student font-bold text-3xl text-slate-900">{t("onboarding.helloLumi")}</div>
        <p className="text-slate-700 mt-2">{t("onboarding.helloBody")}</p>
        <div className="mt-6 grid gap-3">
          <div>
            <Label>{t("onboarding.whatCallYou")}</Label>
            <Input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="onb-display-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("onboarding.ageRange")}</Label>
              <select className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm" value={form.age_range} onChange={(e) => set("age_range", e.target.value)} data-testid="onb-age">
                {AGES.map((a) => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <Label>{t("student.grade")}</Label>
              <select className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm" value={form.grade_level} onChange={(e) => set("grade_level", e.target.value)} data-testid="onb-grade">
                {GRADES.map((g) => <option key={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>{t("onboarding.country")}</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
            <div><Label>{t("onboarding.prefLang")}</Label><Input value={form.language} onChange={(e) => set("language", e.target.value)} /></div>
          </div>
        </div>
      </>
    ),
    (
      <>
        <div className="font-student font-bold text-3xl text-slate-900">{t("onboarding.whatHelp")}</div>
        <p className="text-slate-700 mt-2">{t("onboarding.pickMany")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          {SUBJECTS.map((s) => (
            <button key={s} type="button" data-testid={`onb-subject-${s}`}
                    onClick={() => toggleSubject(s)}
                    className={`p-3 rounded-xl border text-left ${form.subjects.includes(s) ? "border-blue-500 bg-blue-50 text-slate-900" : "border-slate-200 bg-white text-slate-800"}`}>
              <div className="font-semibold text-sm capitalize">{t(`student.tiles.${s}`)}</div>
            </button>
          ))}
        </div>
        <div className="mt-4">
          <Label>{t("onboarding.learningPace")}</Label>
          <select className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm" value={form.learning_pace} onChange={(e) => set("learning_pace", e.target.value)}>
            <option value="gentle">Gentle</option><option value="steady">Steady</option><option value="fast">Fast</option><option value="advanced">Advanced</option>
          </select>
        </div>
      </>
    ),
    (
      <>
        <div className="font-student font-bold text-3xl text-slate-900">{t("onboarding.connectParent")}</div>
        <p className="text-slate-700 mt-2">{t("onboarding.connectParentBody")}</p>
        <div className="mt-3">
          <Label>{t("register.parentCode")}</Label>
          <Input value={form.parent_code} onChange={(e) => set("parent_code", e.target.value)} placeholder="P-XXXXXXXX" data-testid="onb-parent-code" />
        </div>
      </>
    ),
  ];

  return (
    <AppShell isStudent>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 hb-card p-6 flex flex-col items-center">
          <LumiMascot size={200} className="lumi-bounce" />
          <div className="font-student font-bold text-2xl mt-2">{t("onboarding.helloLumi")}</div>
          <div className="text-sm text-slate-600">{t("onboarding.stepOf", { n: step + 1, total: steps.length })}</div>
        </div>
        <div className="lg:col-span-8 hb-card p-8">
          {steps[step]}
          <div className="mt-6 flex justify-between">
            <Button variant="outline" className="rounded-full" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>{t("common.back")}</Button>
            {step < steps.length - 1 ? (
              <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={() => setStep((s) => s + 1)} data-testid="onb-next">{t("common.next")}</Button>
            ) : (
              <Button className="rounded-full bg-green-600 text-white hover:bg-green-700" onClick={finish} data-testid="onb-finish">{t("onboarding.startBtn")}</Button>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
